import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import {
  Header as BrHeader,
  ContextBar,
  Input,
  Layout,
  Logo,
  Modal,
  NotificationCenter,
  Paragraph,
  SiteNav,
  Spinner,
} from '@vwfs-bronson/bronson-react';
import { format } from 'date-fns';
import { getBrandName } from '../../../config';
import {
  isAuthenticationRoute,
  isDashboardHomeRoute,
  isEsignRoute,
  isOutletSelectionRoute,
} from '../../../routing/routes';
import {
  getInitiatorParticipant,
  getReferenceData,
  translateReferenceType,
} from '../../../screens/NotesPage/conversationUtils';
import { notesFiltersInitialValues } from '../../../screens/NotesPage/Filters';
import {
  logoutAndRemoveAuthorizationData,
  redirectToLoginPage,
  resumeDealerJourney,
} from '../../../services/api/authorization';
import { getLogosSrc } from '../../../services/brand';
import { sortObjectsArray } from '../../../services/common/miscUtils';
import { brokerSelectors } from '../../../services/redux/features/broker.slice';
import { customSelectors } from '../../../services/redux/features/custom.slice';
import { loadingActions, loadingSelectors } from '../../../services/redux/features/loading.slice';
import { setFilters, setIntent } from '../../../services/redux/features/notes.slice';
import { notificationCenterSelectors } from '../../../services/redux/features/notificationCenter.slice';
import { useNavigate } from '../../../services/routing/navigate';
import { HeaderTitle } from '../../HeaderTitle/HeaderTitle';
import '../styles.scss';
import { CompanyListItemForModal } from './CompanyListItemForModal';
import { CompanyListItemForNavDropdown } from './CompanyListItemForNavDropdown';
import { formatCompanyLabelForDisplay, getCompanyDisplayText } from './utils';

const navigationWrappingBreakpoint = 1250;

const getInitialShouldUseModalForCompaniesList = (
  allowedCompanies: DealerIntegration.Components.Schemas.MilesBrokerCompaniesData[]
) => {
  return allowedCompanies.length > 5;
};

const getInitialSortedAllowedCompanies = (
  allowedCompanies: DealerIntegration.Components.Schemas.MilesBrokerCompaniesData[],
  selectedMilesCompanyId?: string
) => {
  return sortObjectsArray([...allowedCompanies], (company) =>
    company.milesCompanyId === selectedMilesCompanyId ? '' : company.milesBerCode || ''
  );
};

export const Header: FunctionComponent = () => {
  const { t } = useTranslation();
  const brand = getBrandName();
  const [logoSrc, setLogoSrc] = useState({ normal: '', small: '' });
  const [wrapNavigationItems, setWrapNavigationItems] = useState(true);
  const [companySearchText, setCompanySearchText] = useState<string>('');
  const isMounted = useRef(true);
  const location = useLocation();
  const { pathname } = location;

  const username = useSelector(brokerSelectors.username);
  const selectedCompany = useSelector(brokerSelectors.currentCompany);
  const allowedCompanies = useSelector(brokerSelectors.allowedCompanies);
  const [shouldUseModalForCompaniesList, setShouldUseModalForCompaniesList] = useState<boolean>(() =>
    getInitialShouldUseModalForCompaniesList(allowedCompanies)
  );

  const [showCompanySelectionModal, setShowCompanySelectionModal] = useState<boolean>(false);
  const [sortedAllowedCompanies, setSortedAllowedCompanies] = useState(() =>
    getInitialSortedAllowedCompanies(allowedCompanies, selectedCompany?.milesCompanyId)
  );

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isHybridJourney = useSelector(customSelectors.selectIsHybridJourney);
  const isContextualLoading = useSelector(loadingSelectors.selectIsContextualLoading);

  useEffect(() => {
    setShouldUseModalForCompaniesList(getInitialShouldUseModalForCompaniesList(allowedCompanies));
    setSortedAllowedCompanies(getInitialSortedAllowedCompanies(allowedCompanies, selectedCompany?.milesCompanyId));
    setShowCompanySelectionModal(false);
  }, [allowedCompanies, selectedCompany]);

  useEffect(() => {
    isMounted.current = true;

    Promise.resolve(getLogosSrc()).then((_logoSrc) => {
      if (isMounted.current) {
        setLogoSrc({
          normal: _logoSrc.logoDefault,
          small: _logoSrc.logoSmall,
        });
      }
    });

    return () => {
      isMounted.current = false;
    };
  }, [brand]);

  const conversations = useSelector(notificationCenterSelectors.selectUnreadConversations);

  const mapConversations = (conversations: MilesPosNotesIntegrationService.Components.Schemas.Conversation[]) => {
    if (!Array.isArray(conversations) || conversations.length === 0) {
      return [];
    }

    return conversations
      .filter(
        (conv) =>
          ((conv.unreadMessages ?? 0) > 0 || !conv.isSeenByCurrentUser) &&
          conv.participants?.some((p) => p.isCurrentUser === true)
      )
      .map((conv) => {
        const reference = getReferenceData(conv);
        const message = conv.lastMessage?.message ?? '';
        const participantInitiatorName = getInitiatorParticipant(conv?.participants)?.name ?? '';
        const referenceString = `${reference?.objectType ? translateReferenceType(reference.objectType, t) + ' ' : ''}${reference?.humanReadableObjectReferenceId ?? ''}`;
        const firstUnreadConversationId = conv.conversationId;

        return {
          timestampText: format(new Date(conv.lastMessage?.timeStamp || ''), 'dd.MM.yyyy, HH:mm'),
          id: conv.conversationId ?? '',
          title: conv.title ?? '',
          read: conv.unreadMessages === 0 && conv.isSeenByCurrentUser === true,
          text: JSON.stringify({
            message,
            participantInitiatorName,
            referenceString,
            humanReadableObjectReferenceId: reference?.humanReadableObjectReferenceId,
            objectType: reference?.objectType,
            firstUnreadConversationId,
          }),
          onClick: () => navigate.dashboardConversations(),
        };
      });
  };

  const disableHeaderNavigation = isOutletSelectionRoute() || isDashboardHomeRoute() || isAuthenticationRoute();

  const navigateToDashboard = async () => {
    if (isHybridJourney) {
      await resumeDealerJourney();
      navigate.dashboardHome();
      return;
    }

    try {
      dispatch(loadingActions.setLoading(true));
      navigate.dashboardHome('', { navigatedFromHeader: true });
    } catch {
      navigate.error();
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setWrapNavigationItems(window.innerWidth <= navigationWrappingBreakpoint);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const createQuotation = async () => {
    if (!selectedCompany) return;
    navigate.selectCarType();
  };

  const logout = (e) => {
    e.preventDefault();
    dispatch(loadingActions.setLoading(true));
    logoutAndRemoveAuthorizationData()
      .then(() => redirectToLoginPage())
      .catch(() => {
        dispatch(loadingActions.setLoading(false));
        navigate.error();
      });
  };

  const headerItemClassName = wrapNavigationItems ? '' : 'u-ws-nowrap';

  const showHeaderNavigation = pathname.startsWith('/dashboard');
  const companyLabelWrapBoundary = 18;

  const onHeaderLogoClick = () => {
    if (isEsignRoute()) {
      window.location.href = 'https://www.vwfs.fr/';
    } else {
      navigateToDashboard();
    }
  };

  return (
    <BrHeader
      className="u-m-none"
      testId="appHeader"
      staticHeader
      logo={
        <Logo
          aria-label="Logo"
          onClick={onHeaderLogoClick}
          testId="appHeaderLogo"
          disableLink={disableHeaderNavigation}
          src={logoSrc.normal}
          srcSmall={logoSrc.small}
        />
      }
      notificationCenter={
        showHeaderNavigation && (
          <NotificationCenter notifications={mapConversations(conversations)}>
            {(notification) => {
              const {
                message,
                participantInitiatorName,
                referenceString,
                humanReadableObjectReferenceId,
                objectType,
                firstUnreadConversationId,
              } = JSON.parse(notification?.text ?? '{}');

              return (
                <NotificationCenter.Item
                  timestamp
                  timestampText={notification.timestampText}
                  onClick={() => {
                    dispatch(
                      setFilters({
                        ...notesFiltersInitialValues,
                        objectType,
                        humanReadableObjectReferenceId,
                      })
                    );
                    dispatch(setIntent({ type: 'READ', conversationId: firstUnreadConversationId }));
                    navigate.dashboardConversations();
                  }}
                  showUnreadButton={false}
                >
                  <Paragraph className="u-m-none">{message}</Paragraph>
                  <Paragraph className="u-m-none">
                    <i> {referenceString} </i>
                  </Paragraph>
                  <Paragraph className="u-text-muted u-m-none">{participantInitiatorName}</Paragraph>
                </NotificationCenter.Item>
              );
            }}
          </NotificationCenter>
        )
      }
    >
      <ContextBar isContextBar>
        <HeaderTitle />
      </ContextBar>

      {showHeaderNavigation ? (
        <SiteNav>
          <SiteNav.Area>
            <SiteNav.Item icon label={t('dashboard:header:userMenuLabel')} className={headerItemClassName}>
              <SiteNav.Item.DropdownItem href="" onClick={(e) => e.preventDefault()}>
                {username}
              </SiteNav.Item.DropdownItem>
              <SiteNav.Item.DropdownItem onClick={logout} href="">
                {t('app:header:logout')}
              </SiteNav.Item.DropdownItem>
            </SiteNav.Item>
            <SiteNav.Item
              icon
              label={formatCompanyLabelForDisplay(selectedCompany ?? {}, companyLabelWrapBoundary)}
              className={
                getCompanyDisplayText(selectedCompany)?.length > companyLabelWrapBoundary
                  ? 'u-pl-small u-pr-xxsmall'
                  : headerItemClassName
              }
              onClick={() => {
                setCompanySearchText('');

                if (shouldUseModalForCompaniesList) {
                  setSortedAllowedCompanies(
                    getInitialSortedAllowedCompanies(allowedCompanies, selectedCompany?.milesCompanyId)
                  );
                  setShowCompanySelectionModal(true);
                }
              }}
            >
              {!shouldUseModalForCompaniesList && (
                <>
                  {sortedAllowedCompanies.map((company, i) => (
                    <CompanyListItemForNavDropdown
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${company.milesCompanyId}_${i}`}
                      company={company}
                    />
                  ))}
                </>
              )}
            </SiteNav.Item>

            {showCompanySelectionModal && (
              <Modal
                style={{ overflow: 'hidden' }}
                title={t('dashboard:header:berCodeLabel')}
                onClose={() => {
                  setShowCompanySelectionModal(false);
                }}
                asPortal
                shown
              >
                <div style={{ position: 'sticky', top: 0 }}>
                  <Layout middle className="u-p-xsmall u-bg-pale">
                    <Layout.Item>
                      <Input
                        placeholder={t('dashboard:header:searchText')}
                        value={companySearchText}
                        onChange={(event) => {
                          const text = event.target.value;
                          setCompanySearchText(text);
                          if (text.length === 0) {
                            setSortedAllowedCompanies(
                              getInitialSortedAllowedCompanies(allowedCompanies, selectedCompany?.milesCompanyId)
                            );
                          } else {
                            setSortedAllowedCompanies(
                              sortObjectsArray([...allowedCompanies], (company) =>
                                getCompanyDisplayText(company).toLowerCase().includes(text.toLowerCase()) ? 0 : 1
                              )
                            );
                          }
                        }}
                      />
                    </Layout.Item>
                  </Layout>
                </div>
                {sortedAllowedCompanies.map((company, i) => (
                  <CompanyListItemForModal
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${company.milesCompanyId}_${i}`}
                    company={company}
                    companySearchText={companySearchText}
                  />
                ))}
                {/* TODO Remove when Bronson fixes BRONSD-1042 */}
                {isContextualLoading > 0 && <Spinner fullPage />}
              </Modal>
            )}
            {!isDashboardHomeRoute() && (
              <>
                <SiteNav.Item
                  label={t(`app:header:createQuotation`)}
                  className={headerItemClassName}
                  onClick={() => createQuotation()}
                  href=""
                />
                <SiteNav.Item
                  label={t(`app:header:transferNotice`)}
                  className={headerItemClassName}
                  onClick={() => navigate.dashboardDealerDocuments()}
                  href=""
                />
              </>
            )}
          </SiteNav.Area>
        </SiteNav>
      ) : null}
    </BrHeader>
  );
};
